import { getRecentlyViewedContent } from '@/library/api'
import { defineStore } from 'pinia'
import {
  LibraryContentObject,
  SubjectAlignment,
  LibraryGoal,
  LibraryStrategy,
  LibraryResource,
} from '@/library/library_types'
import { getStrategyTags } from '@/strategies/api'
import { useUserStore } from '@/authentication/user.store'
import { storeToRefs } from 'pinia'
import { isMoreThan30DaysAgo } from '@/util/date'
import { filterGoalsBySubject, filterResourcesBySubject, filterStrategiesBySubject } from '@/util/subject_filters'
import { getSubjectLabelByContentType, deduplicateNoSELSubjects } from '@/contentprofile/no_sel_composable'
import { CONTENT_TYPES } from '@/util/access_visibility'

type ContentObjects = {
  recentlyViewed: LibraryContentObject[],
  previouslyViewed: LibraryContentObject[]
}

export const useRecentlyViewedStore = defineStore('recently_viewed', {
  state: () => ({
    loading: false,
    loadError: false,

    contentObjects: {
      recentlyViewed: [],
      previouslyViewed: []
    } as ContentObjects,

    selectedSubject: '',
    previousSelectedSubject: '',
    subjects: [] as {
      label: string
      value: string,
      count: number
    }[],
    strategySubjects: [] as string[],

    currentContentType: '',
    contentTypeSingular: '',
  }),
  actions: {
    async fetchResources(contentType: string) {
      this.loading = true
      this.resetState()
      this.currentContentType = contentType
      this.setContentTypeSingular()

      const userStore = useUserStore()
      const { userId } = storeToRefs(userStore)

      if (!userId.value || userId.value === '') {
        this.loading = false
        this.loadError = true
        return
      }

      getRecentlyViewedContent(userId.value, this.contentTypeSingular)
        .then((data) => {
          this.loading = false
          if (!data.content || data.content.length < 1) {
            return
          }

          data.content.forEach(async (content) => {
            if (content.content_body_error) {
              return
            }

            content.show_folder_name = true
            if (content.event_time && isMoreThan30DaysAgo(content.event_time)) {
              this.contentObjects.previouslyViewed.push(content)
            } else {
              this.contentObjects.recentlyViewed.push(content)
            }
            
            await this.processSubjectOptions(content)
          })
        }, (err) => {
          this.loading = false
          console.error(err)
          this.loadError = true
        })
    },
    async processSubjectOptions(content: LibraryContentObject) {
      let subjects: string[] = []
      
      switch (this.contentTypeSingular) {
        case 'goal':
          subjects = (content.content_body as LibraryGoal).subject_alignments?.map(
            (alignment: SubjectAlignment) => alignment.subject
          ) || []
          break
        case 'strategy': {
          const strategyTagResponse = await getStrategyTags()
          this.strategySubjects = strategyTagResponse['subjects']
          const tags = (content.content_body as LibraryStrategy).source_strategy.tags || []
          subjects = tags.filter(tag => this.strategySubjects.includes(tag))
          break
        }
        case 'resource':
          subjects = (content.content_body as LibraryResource).subjects || []
          break
      }
      this.addSubjectsToOptions(subjects, this.contentTypeSingular)
    },
    addSubjectsToOptions(subjects: string[], contentType: string = CONTENT_TYPES.GOAL) {
      subjects = deduplicateNoSELSubjects(subjects)
      subjects.forEach((rawSubject: string) => {
        const subject = getSubjectLabelByContentType(contentType, rawSubject)
        if (
          !this.subjects.some(
            (item: { label: string; value: string }) => item.label === subject
          )
        ) {
          this.subjects.push({ value: subject, label: subject, count: 1 })
        } else {
          this.subjects = this.subjects.map((item: { label: string; value: string, count: number }) => {
            if (item.label === subject) {
              return { ...item, count: item.count + 1 }
            } else {
              return item
            }
          })
        }
      })
    },
    setContentTypeSingular() {
      switch (this.currentContentType) {
        case 'resources':
          this.contentTypeSingular = 'resource'
          break
        case 'strategies':
          this.contentTypeSingular = 'strategy'
          break
        case 'goals':
          this.contentTypeSingular = 'goal'
          break
        default:
          this.contentTypeSingular = ''
      }
    },
    resetState() {
      this.contentObjects = {
        recentlyViewed: [],
        previouslyViewed: []
      } as ContentObjects
      this.subjects = []
      this.selectedSubject = ''
      this.previousSelectedSubject = ''
    }
  },
  getters: {
    filteredContentObjects(): ContentObjects {
      if (!this.selectedSubject) {
        return this.contentObjects
      }

      const contentType = this.contentTypeSingular
      const subject = this.selectedSubject

      function filterContentObjects(contentObjectsToFilter: LibraryContentObject[]): LibraryContentObject[] {
        switch (contentType) {
          case 'goal':
            return filterGoalsBySubject(contentObjectsToFilter, subject)
          case 'strategy':
            return filterStrategiesBySubject(contentObjectsToFilter, subject)
          case 'resource':
            return filterResourcesBySubject(contentObjectsToFilter, subject)
          default:
            return contentObjectsToFilter
        }
      }
      return {
        recentlyViewed: filterContentObjects(this.contentObjects.recentlyViewed),
        previouslyViewed: filterContentObjects(this.contentObjects.previouslyViewed),
      }
    },
    possibleSubjects(): { value: string, label: string }[] {
      const subj = this.subjects.sort((a, b) => b.count - a.count)
      const subjectsToReturn = subj.reduce((accumulator, current) => {
        if (current.value !== '') {
          accumulator.push({ label: current.label, value: current.value })
        }
        return accumulator
      }, [] as { value: string, label: string, separator?: boolean }[])
      subjectsToReturn.unshift({ label: 'All Subjects', value: '', separator: true })
      return subjectsToReturn
    },
    recentlyViewed(): LibraryContentObject[] {
      return this.filteredContentObjects.recentlyViewed
    },
    previouslyViewed(): LibraryContentObject[] {
      return this.filteredContentObjects.previouslyViewed
    },
    hasRecentlyViewed(): boolean {
      return this.filteredContentObjects.recentlyViewed && this.filteredContentObjects.recentlyViewed.length > 0
    },
    hasPreviouslyViewed(): boolean {
      return this.filteredContentObjects.previouslyViewed && this.filteredContentObjects.previouslyViewed.length > 0
    }
  }
})